import { useState } from 'react';
import { createPortal } from 'react-dom';
import api from 'services/api';
import ReportDataModal from './ReportDataModal';
import styles from './AssemblyReport.module.css';

// La respuesta de error de `POST …/pdf` llega como Blob (responseType: 'blob'): se lee para mostrar el motivo REAL que dio el servidor
// (antes se descartaba y se mostraba un texto fijo sobre Python y ReportLab, que el backend ya no usa: el PDF se genera con pdfkit).
async function mensajeDeError(err, respaldo) {
  const d = err?.response?.data;
  try {
    if (d instanceof Blob) return JSON.parse(await d.text()).message || respaldo;
    if (d?.message) return d.message;
  } catch { /* el cuerpo no era JSON: se usa el texto de respaldo */ }
  return respaldo;
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function AssemblyReport({ assemblyId, compact = false }) {
  const [showModal,   setShowModal]   = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [generating,  setGenerating]  = useState(false);
  const [reportData,  setReportData]  = useState(null);
  const [error,       setError]       = useState('');
  const [errorGenerar, setErrorGenerar] = useState('');   // error de generar el PDF: se muestra DENTRO del modal

  // Cargar datos al abrir el modal
  const handleOpen = async () => {
    setError(''); setErrorGenerar(''); setLoading(true);
    try {
      const { data } = await api.get(`/reports/assembly/${assemblyId}`);
      setReportData(data);
      setShowModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar datos del acta');
    } finally {
      setLoading(false);
    }
  };

  // Generar PDF y descargar
  const handleGenerate = async (formData) => {
    setGenerating(true);
    setErrorGenerar('');
    try {
      const response = await api.post(
        `/reports/assembly/${assemblyId}/pdf`,
        formData,
        { responseType: 'blob' }
      );

      // Crear URL temporal y simular clic de descarga
      const url      = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link     = document.createElement('a');
      const asm      = reportData?.assembly;
      const filename = `Acta_Asamblea_${asm?.number_assembly || assemblyId}_${(asm?.property?.name || 'EVENTIFY').replace(/\s+/g, '_')}.pdf`;
      link.href      = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowModal(false);
    } catch (err) {
      setErrorGenerar(await mensajeDeError(err, 'No se pudo generar el PDF. Inténtalo de nuevo.'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      {compact ? (
        <button
          className={`btn btn-sm btn-secondary btn-icon ${styles.botonCompacto}`}
          onClick={handleOpen}
          disabled={loading}
          title="Recuperar acta PDF">
          {loading
            ? <i className="fas fa-spinner fa-spin" />
            : <i className="fas fa-file-pdf" />}
        </button>
      ) : (
        <button className={`btn btn-primary ${styles.botonGenerar}`}
          onClick={handleOpen} disabled={loading}>
          {loading
            ? <><i className="fas fa-spinner fa-spin" /> Cargando...</>
            : <><i className="fas fa-file-pdf" /> Generar acta PDF</>}
        </button>
      )}

      {error && (
        <div className={`alert alert-danger ${styles.aviso}`}>
          <i className={`fas fa-circle-exclamation ${styles.iconoAviso}`} />{error}
        </div>
      )}

      {showModal && reportData && createPortal(
        <ReportDataModal
          assembly={reportData.assembly}
          onClose={() => { if (!generating) { setShowModal(false); setErrorGenerar(''); } }}
          onGenerate={handleGenerate}
          generating={generating}
          error={errorGenerar}
        />,
        document.body
      )}
    </>
  );
}